import { Suspense, lazy } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { CircularProgress, Box } from '@mui/material';
import { RequireAuth } from '@/features/auth/RequireAuth';

// Lazy load pages for better performance
const HomePage = lazy(() => import('@/pages/HomePage'));
const TransactionsPage = lazy(() => import('@/pages/TransactionsPage'));
const CategoriesPage = lazy(() => import('@/pages/CategoriesPage'));
const AccountsPage = lazy(() => import('@/pages/AccountsPage'));
const AnalyticsPage = lazy(() => import('@/pages/AnalyticsPage'));
const SettingsPage = lazy(() => import('@/pages/SettingsPage'));
const NotFoundPage = lazy(() => import('@/pages/NotFoundPage'));
const AboutPage = lazy(() => import('@/pages/AboutPage'));

/**
 * Loading fallback component for route-based code splitting
 */
const RouteLoadingFallback = () => (
  <Box
    display='flex'
    justifyContent='center'
    alignItems='center'
    minHeight='200px'
    role='status'
    aria-label='Loading page'
  >
    <CircularProgress />
  </Box>
);

/**
 * Application router configuration
 * Defines all application routes and navigation
 */
export const AppRouter = () => {
  return (
    <Suspense fallback={<RouteLoadingFallback />}>
      <Routes>
        {/* Home/Dashboard - Default route (public) */}
        <Route path='/' element={<HomePage />} />

        {/* Protected application routes */}
        <Route
          path='/transactions'
          element={
            <RequireAuth>
              <TransactionsPage />
            </RequireAuth>
          }
        />
        <Route
          path='/categories'
          element={
            <RequireAuth>
              <CategoriesPage />
            </RequireAuth>
          }
        />
        <Route
          path='/accounts'
          element={
            <RequireAuth>
              <AccountsPage />
            </RequireAuth>
          }
        />
        <Route
          path='/analytics'
          element={
            <RequireAuth>
              <AnalyticsPage />
            </RequireAuth>
          }
        />
        <Route
          path='/settings'
          element={
            <RequireAuth>
              <SettingsPage />
            </RequireAuth>
          }
        />
        <Route
          path='/about'
          element={
            <RequireAuth>
              <AboutPage />
            </RequireAuth>
          }
        />

        {/* Catch-all route for 404 (public) */}
        <Route path='/404' element={<NotFoundPage />} />
        <Route path='*' element={<Navigate to='/404' replace />} />
      </Routes>
    </Suspense>
  );
};

export default AppRouter;
